import { Link } from '@tanstack/react-router';
import { clsx } from 'clsx';
import {
  LayoutDashboard, Users, Phone, Calendar, FileText, BarChart3,
  Puzzle, Megaphone, Settings, Shield, ClipboardList, ChevronRight,
  PhoneCall, LogOut, User2, Activity,
} from 'lucide-react';
import { useRBAC } from '@/hooks/useRBAC';
import { useAuth } from '@/hooks/useAuth';
import { canAccess } from '@/utils/permissions';
import { ROLE_LABELS } from '@/utils/permissions';
import { getInitials } from '@/utils/formatters';
import type { Role } from '@/types';

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  section: string;
  badge?: number;
}

const navItems: NavItem[] = [
  { href: '/', label: 'Dashboard', icon: <LayoutDashboard size={18} />, section: 'dashboard' },
  { href: '/leads', label: 'Leads', icon: <Users size={18} />, section: 'leads' },
  { href: '/calls', label: 'Live Calls', icon: <PhoneCall size={18} />, section: 'calls' },
  { href: '/appointments', label: 'Appointments', icon: <Calendar size={18} />, section: 'appointments' },
  { href: '/documents', label: 'Documents', icon: <FileText size={18} />, section: 'documents' },
  { href: '/reports', label: 'Reports', icon: <BarChart3 size={18} />, section: 'reports' },
  { href: '/integrations', label: 'Integrations', icon: <Puzzle size={18} />, section: 'integrations' },
  { href: '/ads', label: 'Ads Manager', icon: <Megaphone size={18} />, section: 'ads' },
  { href: '/admin', label: 'User Management', icon: <Users size={18} />, section: 'admin' },
  { href: '/templates', label: 'Templates', icon: <ClipboardList size={18} />, section: 'templates' },
  { href: '/audit', label: 'Audit Logs', icon: <Shield size={18} />, section: 'audit' },
];

interface SidebarProps {
  collapsed?: boolean;
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const { role, user } = useRBAC();
  const { logout } = useAuth();

  const filteredNav = navItems.filter(item => canAccess(role as Role, item.section));

  return (
    <aside className={clsx(
      'flex flex-col bg-white h-screen transition-all duration-300 border-r border-slate-200',
      collapsed ? 'w-16' : 'w-64',
    )}>
      {/* Logo */}
      <div className={clsx('flex items-center border-b border-slate-200 py-4', collapsed ? 'px-3 justify-center' : 'px-5')}>
        {collapsed ? (
          <img src="/logos/light bg sq@4x.png" alt="HeptaCare" className="w-8 h-8 object-contain" />
        ) : (
          <img src="/logos/light bg logo@4x.png" alt="HeptaCare Health CRM" className="h-8 object-contain" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <div className="space-y-1">
          {filteredNav.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600',
                'transition-all duration-150 group relative',
                'hover:bg-teal-50 hover:text-teal-700',
                '[&.active]:bg-teal-50 [&.active]:text-teal-700',
              )}
              activeProps={{ className: 'bg-teal-50 text-teal-700' }}
            >
              <span className="shrink-0 opacity-80 group-hover:opacity-100">{item.icon}</span>
              {!collapsed && (
                <>
                  <span className="text-sm font-medium truncate">{item.label}</span>
                  {item.badge != null && item.badge > 0 && (
                    <span className="ml-auto bg-teal-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          ))}
        </div>

        {/* Settings */}
        <div className="mt-4 pt-4 border-t border-slate-200 space-y-1">
          <Link
            to="/settings"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-teal-50 hover:text-teal-700 transition-all"
          >
            <Settings size={18} className="shrink-0 opacity-80" />
            {!collapsed && <span className="text-sm font-medium">Settings</span>}
          </Link>
        </div>
      </nav>

      {/* User Profile */}
      <div className={clsx('border-t border-slate-200 p-3', collapsed ? 'flex justify-center' : '')}>
        <div className={clsx('flex items-center gap-3', collapsed ? 'flex-col' : '')}>
          <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold shrink-0">
            {getInitials(user?.name)}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{role ? ROLE_LABELS[role as Role] : ''}</div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={() => logout()}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
